import { Component, ChangeDetectionStrategy, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FnmaHeaderComponent } from '@fm-adk-primeng/components-4';
import { MenuItem } from 'primeng/api';
import { applicationUrls } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { CUSTODIAN_ROLES } from '../../../core/constants/iam-roles.constants';

/**
 * GlobalNavComponent - global header navigation.
 * Requirements: 8.1, 8.2, 8.3
 */
@Component({
  selector: 'app-global-nav',
  standalone: true,
  imports: [CommonModule, FnmaHeaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './global-nav.component.html',
  styleUrls: ['./global-nav.component.scss']
})
export class GlobalNavComponent implements OnInit {
  private appUrls = applicationUrls[window.location.hostname] ?? applicationUrls.localhost;
  private authService = inject(AuthService);
  readonly localDevPersonaItems = signal<MenuItem[]>([]);

  readonly isExternalUser = computed(() => this.authService.isExternalUser?.() ?? false);
  readonly isCustodian = computed(() => this.authService.hasRole?.(...CUSTODIAN_ROLES) ?? false);
  readonly firstLevelMenu = computed(() => {
    if (this.isCustodian()) {
      return this.custodianFirstLevelMenu();
    }
    return this.isExternalUser() ? this.externalFirstLevelMenu() : this.internalFirstLevelMenu();
  });

  private internalFirstLevelMenu() {
    return [
      { title: 'DUS Gateway', command: () => this.navigateExternal(this.appUrls.dusGatewayUrl), list: [] },
      { title: 'DUS Docway', command: () => this.navigateExternal(this.appUrls.dusDocwayUrl), list: [] },
      { title: 'DUS Riskway', command: () => this.navigateExternal(this.appUrls.dusRiskwayUrl), list: [] },
      { title: 'DUS 360', command: () => this.navigateExternal(this.appUrls.dus360Url), list: [] },
      { title: 'DUS Navigate', command: () => this.navigateExternal(this.appUrls.dusNavigateUrl), list: [] },
      { title: 'ECHO', command: () => this.navigateExternal(this.appUrls.echoUrl), list: [] },
      {
        title: 'Useful Links',
        icon: 'pi pi-chevron-down',
        iconPosition: 'right',
        list: [
          {
            title: 'Link1',
            command: () => this.navigateExternal(this.appUrls.link1),
          },
        ]
      }
    ];
  }

  private externalFirstLevelMenu() {
    return [
      {
        title: 'DUS Docway',
        command: () => this.navigateExternal(this.getExternalUrl('dusDocwayUrl', 'dusDocWayUrl')),
        list: []
      },
      {
        title: 'DUS Gateway',
        command: () => this.navigateExternal(this.getExternalUrl('dusGatewayUrl')),
        list: []
      },
      this.externalDusNavigateItem(),
      {
        title: 'LDI',
        command: () => this.navigateExternal(this.getExternalUrl('ldiUrl')),
        list: []
      }
    ];
  }

  private custodianFirstLevelMenu() {
    return [this.externalDusNavigateItem()];
  }

  private externalDusNavigateItem() {
    return {
      title: 'DUS Navigate',
      command: () => this.navigateExternal(this.getExternalUrl('dusNavigateUrl')),
      list: []
    };
  }

  private navigateExternal(url: string): void {
    if (!url) {
      return;
    }

    window.open(url, '_blank');
  }

  private getExternalUrl(primaryKey: string, fallbackKey?: string): string {
    return (
      this.appUrls?.[primaryKey] ??
      (fallbackKey ? this.appUrls?.[fallbackKey] : undefined) ??
      ''
    );
  }

  readonly username = computed(() => {
    const details = this.authService.getUserInfo()?.userDetails;
    const first = details?.firstName?.trim() ?? '';
    const last = details?.lastName?.trim() ?? '';
    const fullName = `${first} ${last}`.trim();
    return fullName || details?.uid || 'User';
  });

  readonly profileMenuItems = computed<MenuItem[]>(() => {
    const details = this.authService.getUserInfo()?.userDetails;
    const isInternalUser = details?.isInternalUser === true;
    const role = details?.groups?.[0] ?? 'Not Available';
    const organization = details?.dealerOrgName ?? 'Not Available';
    const mornetId = details?.mornetID ?? 'Not Available';
    const userId = details?.uid ?? 'Not Available';
    const localPersonaMenu = this.localDevPersonaItems();

    return [
      { label: this.username(), disabled: true },
      { label: `User Id: ${userId}`, disabled: true },
      { label: `Role: ${role}`, disabled: true },
      ...(!isInternalUser
        ? [
            { label: `Organization: ${organization}`, disabled: true },
            { label: `Mornet Id: ${mornetId}`, disabled: true }
          ]
        : []),
      ...(localPersonaMenu.length > 0 ? [{ separator: true }, ...localPersonaMenu] : []),
      { separator: true },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => {
          this.logout();
        },
      }
    ];
  });

  async ngOnInit(): Promise<void> {
    if (!this.authService.isLocalDevMode()) {
      return;
    }

    const selectedLabel = this.authService.getSelectedLocalDevUserLabel();
    const personaLabels = await this.authService.getLocalDevPersonaLabels();
    if (personaLabels.length === 0) {
      return;
    }

    this.localDevPersonaItems.set([
      { label: 'Local Persona', disabled: true },
      ...personaLabels.map((label) => ({
        label: selectedLabel === label ? `${label} (active)` : label,
        command: () => this.authService.switchLocalDevPersona(label),
      }))
    ]);
  }

  logout() {
    this.authService.logout();
  }
}